import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Política de Privacidad | Aivanilo';
  }, []);

  return (
    <div className="bg-white min-h-screen">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Aivanilo — Inicio" data-testid="privacy-logo">
            <img src="/aivanilo-logo.png" alt="Aivanilo" className="h-8 w-auto" width="180" height="40" />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-6 py-16 md:py-24"
      >
        <p className="text-sm text-blue-600 font-semibold mb-3 tracking-wide uppercase">Legal</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Política de Privacidad</h1>
        <p className="text-slate-500 text-sm mb-12">Última actualización: diciembre de 2025</p>

        <Section title="1. Responsable del tratamiento">
          <p>
            Aivanilo es el responsable del tratamiento de los datos personales que nos facilitas a través
            de esta web. Para cualquier cuestión relacionada con tus datos puedes contactarnos en{' '}
            <a href="mailto:aivaniloinfo@gmail.com" className="text-blue-600 hover:underline">aivaniloinfo@gmail.com</a>.
          </p>
        </Section>

        <Section title="2. Datos que recogemos">
          <p>
            A través del formulario de contacto («Reservar consulta») recogemos únicamente los datos que
            introduces voluntariamente: nombre, correo electrónico y, de forma opcional, teléfono, empresa
            y el mensaje que nos escribas. También registramos la fecha y la aceptación de esta política.
          </p>
        </Section>

        <Section title="3. Finalidad del tratamiento">
          <p>
            Utilizamos tus datos exclusivamente para atender tu solicitud, responder a tus consultas y, en
            su caso, valorar y proponer soluciones de automatización e inteligencia artificial para tu
            empresa. No los empleamos para ninguna otra finalidad sin tu consentimiento.
          </p>
        </Section>

        <Section title="4. Base legal">
          <p>
            La base legal para el tratamiento es tu consentimiento, que otorgas al marcar la casilla de
            aceptación y enviar el formulario. Puedes retirar dicho consentimiento en cualquier momento.
          </p>
        </Section>

        <Section title="5. Conservación de los datos">
          <p>
            Conservaremos tus datos durante el tiempo necesario para gestionar tu solicitud y, posteriormente,
            durante el plazo legalmente exigible. Cuando dejen de ser necesarios, se eliminarán de forma segura.
          </p>
        </Section>

        <Section title="6. Destinatarios">
          <p>
            No cedemos tus datos a terceros salvo obligación legal. Para el envío de notificaciones por correo
            electrónico podemos apoyarnos en proveedores de servicios que actúan como encargados del tratamiento
            y cumplen la normativa de protección de datos aplicable.
          </p>
        </Section>

        <Section title="7. Tus derechos">
          <p>
            Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad
            de tus datos escribiéndonos a{' '}
            <a href="mailto:aivaniloinfo@gmail.com" className="text-blue-600 hover:underline">aivaniloinfo@gmail.com</a>.
            Si consideras que no hemos tratado tus datos correctamente, tienes derecho a presentar una reclamación
            ante la autoridad de control competente.
          </p>
        </Section>

        <Section title="8. Cambios en esta política">
          <p>
            Podemos actualizar esta Política de Privacidad para adaptarla a novedades legislativas o a cambios en
            nuestros servicios. Te recomendamos revisarla periódicamente.
          </p>
        </Section>

        <div className="mt-12 p-6 bg-slate-50 border border-slate-200 rounded-2xl flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-1">¿Tienes dudas sobre tus datos?</p>
            <p className="text-slate-600 text-sm">
              Escríbenos a{' '}
              <a href="mailto:aivaniloinfo@gmail.com" className="text-blue-600 hover:underline">aivaniloinfo@gmail.com</a>{' '}
              y te ayudaremos lo antes posible.
            </p>
          </div>
        </div>
      </motion.main>

      <footer className="border-t border-slate-200 py-8 text-center">
        <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Aivanilo. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
